'use client'

import { useState, useMemo } from 'react'
import Image from 'next/image'
import { useMatches } from '@/hooks/useDatabase'
import { MatchFilters, Match } from '@/lib/database'
import { generateMatchUrl } from '@/lib/slug-utils'

export default function HomeMatchCards() {
  const [activeTab, setActiveTab] = useState('football')
  
  // Get API domain from environment
  const apiDomain = process.env.NEXT_PUBLIC_API_DOMAIN || 'https://10594c65be5.vws.vegacdn.vn'
  
  // Get all matches first
  const allMatchesFilters = useMemo(() => ({
    type: 'liveAll' as MatchFilters['type'],
    limit: 1000 // Lấy tất cả matches
  }), [])
  
  const { matches: allMatches, loading, error } = useMatches(allMatchesFilters)

  // Filter matches based on active tab
  const displayMatches = useMemo(() => {
    switch (activeTab) {
      case 'football':
        return allMatches.filter(m => m.match_type === '1')
      case 'basketball':
        return allMatches.filter(m => m.match_type === '2')
      case 'esports':
        return allMatches.filter(m => m.match_type === '3')
      case 'other':
        return allMatches.filter(m => m.match_type === '4' || !m.match_type || m.match_type === '0')
      default:
        return allMatches
    }
  }, [allMatches, activeTab])

  // Get counts for each sport type from all matches
  const getCounts = useMemo(() => {
    return {
      football: allMatches.filter(m => m.match_type === '1').length,
      basketball: allMatches.filter(m => m.match_type === '2').length,
      esports: allMatches.filter(m => m.match_type === '3').length,
      other: allMatches.filter(m => m.match_type === '4' || !m.match_type || m.match_type === '0').length
    }
  }, [allMatches])
  
  // Format time function similar to Schedule component
  const formatMatchTime = (timeStart: string | undefined, date: string | undefined) => {
    if (!timeStart && !date) return '--:--'
    
    try {
      // If we have time_start, use it directly
      if (timeStart) {
        const matchDate = new Date(timeStart)
        
        const timeStr = matchDate.toLocaleTimeString('vi-VN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
        
        const dayMonth = matchDate.toLocaleDateString('vi-VN', {
          day: '2-digit',
          month: '2-digit'
        })
        
        return `${timeStr} - ${dayMonth}`
      }
      
      // Fallback to old format if only date is available
      return date || '--:--'
    } catch (error) {
      return '--:--'
    }
  }

  // Helper function to get full URL for avatars
  const getFullAvatarUrl = (avatarPath: string | undefined) => {
    console.log('Caster avatar path:', avatarPath); // Debug log
    if (!avatarPath) return '/images/caster-avatar.png' // Default fallback
    if (avatarPath.startsWith('http')) return avatarPath // Already full URL
    
    // avatarPath already contains the full path like "/storage/photos/user/images/..."
    return `https://10594c65be5.vws.vegacdn.vn${avatarPath}` // Build full URL without duplicate path
  }
  
  // Update tab config to use dynamic counts with loading fallback
  const tabConfig = useMemo(() => [
    {
      id: 'football',
      name: 'Bóng Đá',
      count: loading ? 0 : getCounts.football,
      icon: (
        <i className="fas fa-futbol"></i>
      )
    },
    {
      id: 'basketball',
      name: 'Bóng Rổ',
      count: loading ? 0 : getCounts.basketball,
      icon: (
        <i className="fas fa-basketball-ball"></i>
      )
    },
    {
      id: 'esports',
      name: 'Esport',
      count: loading ? 0 : getCounts.esports,
      icon: (
        <i className="fas fa-gamepad"></i>
      )
    },
    {
      id: 'other',
      name: 'Khác',
      count: loading ? 0 : getCounts.other,
      icon: (
        <i className="fas fa-ellipsis-h"></i>
      )
    }
  ], [getCounts, loading])

  // Skeleton Card Component
  const SkeletonCard = () => (
    <div className="my-2">
      <div className="card-item skeleton-card">
        {/* Card Header Skeleton */}
        <div className="d-flex justify-between items-center card-header">
          <div className="league skeleton-league">
            <div className="skeleton-text skeleton-league-text"></div>
          </div>
          <div className="time-status-wrapper d-flex items-center gap-2">
            <div className="skeleton-time"></div>
          </div>
        </div>

        {/* Card Body Skeleton */}
        <div className="card-body flex justify-center items-center gap-4">
          {/* Home Team Skeleton */}
          <div className="home-team d-flex items-center justify-end gap-2" style={{ width: '35%' }}>
            <div className="skeleton-text skeleton-team-name"></div>
            <div className="skeleton-avatar"></div>
          </div>

          {/* VS Section Skeleton */}
          <div className="vs text-center d-block" style={{ width: '20%' }}>
            <div className="skeleton-vs-icon"></div>
            <div className="skeleton-vs-text"></div>
            <div className="info-match mt-1">
              <div className="d-flex items-center">
                <div className="d-flex items-center gap-2 info-conner w-50 justify-center">
                  <div className="skeleton-icon"></div>
                  <div className="skeleton-score"></div>
                </div>
                <div className="d-flex items-center gap-2 info-conner w-50 justify-center">
                  <div className="skeleton-icon"></div>
                  <div className="skeleton-score"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Away Team Skeleton */}
          <div className="away-team d-flex items-center justify-start gap-2" style={{ width: '35%' }}>
            <div className="skeleton-avatar"></div>
            <div className="skeleton-text skeleton-team-name"></div>
          </div>
        </div>

        {/* Card Footer Skeleton */}
        <div className="card-footer flex justify-between items-center">
          <div className="skeleton-caster-icon"></div>
          <div className="caster-info d-flex justify-center items-center gap-2 ps-5">
            <div className="skeleton-caster-avatar"></div>
            <div className="skeleton-text skeleton-caster-name"></div>
          </div>
          <div className="skeleton-banner"></div>
        </div>
      </div>
    </div>
  )

  return (
    <>
    <div className="w-full" data-testid="match-list">
      <div className="w-full mx-auto ">
        <div className="content-homepage">
          {/* Tab Navigation */}
          <ul className="nav nav-tabs tab-menu flex flex-wrap justify-between items-center mb-6" style={{
            background: 'rgba(255, 170, 2, 1)',
            padding: '8px 8px 0 8px',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            borderBottom: '1px solid white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            height: 'auto',
            width: '100%'
          }}>
            <div className="tab-items-group flex" style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: '0',
              flex: '6'
            }}>
              {tabConfig.map((tab) => (
                <li key={tab.id} className="nav-item tab-menu-item flex-1" style={{
                  minHeight: '60px',
                  margin: '0',
                  flex: '1',
                  maxWidth: '120px'
                }}>
                  <button
                    className={`nav-link group w-full flex flex-col items-center gap-2 p-4 border-none bg-transparent transition-all duration-200 ${
                      activeTab === tab.id ? 'active text-[#FC5D1D]' : 'text-white hover:text-[#FC5D1D]'
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                    type="button"
                    style={{
                      backgroundColor: activeTab === tab.id ? 'white' : 'transparent',
                      color: activeTab === tab.id ? 'rgba(255, 170, 2, 1)' : 'white',
                      lineHeight: 'normal',
                      textTransform: 'capitalize',
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '8px 12px',
                      transition: 'all 0.3s ease',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      borderBottomLeftRadius: '0',
                      borderBottomRightRadius: '0',
                      border: 'none',
                      margin: '0'
                    }}
                  >
                    <div className={`${activeTab === tab.id ? 'text-[#FC5D1D]' : 'text-white group-hover:text-[#FC5D1D]'}`} style={{
                      color: activeTab === tab.id ? 'rgba(252, 93, 29, 1)' : 'inherit'
                    }}>
                      {tab.icon}
                    </div>
                    <span className="text-lg font-semibold" style={{
                      color: 'white',
                      background: 'rgba(252, 93, 29, 1)',
                      borderRadius: '50%',
                      fontSize: '10px',
                      minWidth: '18px',
                      height: '18px',
                      lineHeight: '18px',
                      position: 'absolute',
                      right: '8px',
                      top: '4px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px'
                    }}>{tab.count}</span>
                    <p className="text-sm mb-0" style={{
                      margin: '0',
                      fontSize: '14px',
                      fontWeight: '600',
                      textAlign: 'center',
                      color: activeTab === tab.id ? 'rgba(255, 170, 2, 1)' : 'inherit'
                    }}>{tab.name}</p>
                  </button>
                </li>
              ))}
            </div>
            
            {/* Betting Banner */}
            <div className="betting-banner" style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: 'auto',
              flex: '4',
              justifyContent: 'flex-end'
            }}>
              <a 
                href="https://okwintv01.okfun.vin/register.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="img-menu-home ml-4"
              >
                <Image
                  src="/images/okfun_18tr.gif"
                  alt="Betting"
                  width={250}
                  height={50}
                  className="h-12 w-auto"
                  unoptimized
                />
              </a>
            </div>
          </ul>

          {/* Tab Content */}
          <div className="tab-content w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {loading ? (
                  Array.from({ length: 4 }).map((_, index) => <SkeletonCard key={index} />)
                ) : (
                  displayMatches.map((match) => {
                    // Debug log to check match data
                    console.log('Match data:', match);
                    
                    return (
                      <div key={match.id} className="my-2">
                          <div 
                            className="card-item pointer"
                            style={{
                              backgroundImage: 'url(https://10592a1a07e.vws.vegacdn.vn/okwinstatic/images/bg-football.webp)',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '100% 100%'
                            }}
                            onClick={() => {
                              const matchUrl = generateMatchUrl(
                                match.id,
                                match.team_hone || 'Đội nhà',
                                match.team_away || 'Đội khách',
                                match.league_name,
                                match.time_start,
                                match.caster
                              )
                              window.location.href = matchUrl
                            }}
                          >
                            {/* Card Header */}
                            <div className="d-flex justify-between items-center card-header">
                              <div className="league">
                                <div className="league-text">
                                  {match.league_name || 'Unknown League'}
                                </div>
                              </div>
                              <div className="time-status-wrapper d-flex items-center gap-2">
                                {match.match_status === 'live' ? (
                                  <>
                                    <div className="time-match">
                                      {formatMatchTime(match.time_start, match.time)}
                                    </div>
                                    <img className="live-icon" src="/images/live.png" alt="Live" />
                                  </>
                                ) : (
                                  <div className="time-match">
                                    {formatMatchTime(match.time_start, match.time)}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Card Body */}
                            <div className="card-body flex justify-center items-center gap-4">
                              {/* Home Team */}
                              <div className="home-team d-flex items-center justify-end gap-2" style={{ width: '35%' }}>
                                <p className="name-team line-clamp-1">
                                  {match.team_hone}
                                </p>
                                <img
                                  className="logo-team"
                                  src={match.team_hone_avatar ? `${apiDomain}${match.team_hone_avatar}` : `${apiDomain}/images/teams/default.png`}
                                  alt={match.team_hone || ''}
                                />
                              </div>

                              {/* VS Section */}
                              <div className="vs text-center d-block" style={{ width: '20%' }}>
                                <div>
                                  <img className="ods-icon" src="/images/ods-icon.png" alt="" />
                                </div>
                                <div>
                                  <img className="icon-vs" src="/images/vs-icon.png" alt="" />
                                </div>
                                <div className="info-match mt-1">
                                  <div className="d-flex items-center">
                                    <div className="d-flex items-center gap-2 info-conner w-50 justify-center">
                                      <i className="fas fa-flag"></i>
                                      <span>0-0</span>
                                    </div>
                                    <div className="d-flex items-center gap-2 info-conner w-50 justify-center">
                                      <i className="fas fa-square yellow-card"></i>
                                      <span>0-0</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Away Team */}
                              <div className="away-team d-flex items-center justify-start gap-2" style={{ width: '35%' }}>
                                <img
                                  className="logo-team"
                                  src={match.team_away_avatar ? `${apiDomain}${match.team_away_avatar}` : `${apiDomain}/images/teams/default.png`}
                                  alt={match.team_away || ''}
                                />
                                <p className="name-team line-clamp-1">
                                  {match.team_away}
                                </p>
                              </div>
                            </div>

                            {/* Card Footer */}
                            <div className="card-footer flex justify-between items-center">
                              <img className="caster-icon" src="/images/caster-icon.png" alt="" />
                              <div className="caster-info d-flex justify-center items-center gap-2 ps-5">
                                <img
                                  className="caster-avatar"
                                  src={getFullAvatarUrl(match.caster_avatar)}
                                  alt="Caster"
                                />
                                <p>{match.caster || ''}</p>
                              </div>
                              <a 
                                target="_blank" 
                                href="https://okwintv01.okfun.vin/register.html"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <img className="img-ods pointer" src="/images/okfun_18tr.gif" alt="cakhiatv" />
                              </a>
                            </div>
                          </div>
                      </div>
                    );
                  })
                )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <style jsx>{`
        .card-item {
          height: 180px;
          box-shadow: 0px 4px 0px 0px rgba(179, 119, 0, 1);
          border-radius: 10px;
          position: relative;
        }
        .card-header:first-child {
          border-radius: var(--bs-card-inner-border-radius) var(--bs-card-inner-border-radius) 0 0;
        }
        .justify-between {
          justify-content: space-between;
        }
        .items-center {
          align-items: center !important;
        }
        .d-flex {
          display: flex !important;
        }
        .card-header {
        }
        .card-item .league {
          background: rgba(255, 170, 2, 1);
          width: 154px;
          text-align: center;
          border-bottom-right-radius: 10px;
          height: 30px;
          border-top-left-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          padding: 0 8px;
        }
        .card-item .league-text {
          font-size: 16px;
          font-weight: 700;
          color: white;
          overflow: hidden;
          white-space: nowrap;
        }
        .card-item .time-match {
          background: linear-gradient(91.15deg, rgba(0, 0, 0, 0.6) 4.56%, rgba(102, 102, 102, 0.6) 94.01%);
          border: 1px solid #E09603;
          width: 81px;
          height: 20px;
          font-size: 10px;
          border-radius: 30px;
          font-weight: 700;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .card-item .live-icon {
          width: 40px;
          height: 20px;
          object-fit: contain;
        }
        .time-status-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-right: 10px;
        }
        .card-item .name-team {
          font-size: 16px;
          font-weight: 700;
          color: white;
        }
        .card-item .logo-team {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          object-fit: cover;
          background: white;
        }
        .card-item .card-body {
          height: 83px;
          padding: 8px 12px;
        }
        .card-item .info-match {
          background: linear-gradient(360deg, rgba(69, 67, 67, 0.4) 10%, rgba(105, 103, 103, 0.8) 100%) !important;
          border-top-left-radius: 0 !important;
          border-top-right-radius: 0 !important;
          border-bottom-left-radius: 5px !important;
          border-bottom-right-radius: 5px !important;
          border-top: 1px solid white !important;
          height: 20px !important;
          width: 100px !important;
          margin: 5px auto 0 auto !important;
          position: relative !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .card-item .info-match .d-flex {
          height: 100% !important;
          width: 100% !important;
        }
        .card-item .info-match .info-conner:first-child {
          border-right: 1px solid #ccc !important;
        }
        .card-item .info-match .info-conner {
          height: 100% !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 0 !important;
        }
        .card-item .info-match .info-conner img {
          width: 8px !important;
          height: 8px !important;
          opacity: 1 !important;
          transition: none !important;
          display: inline-block !important;
          max-width: none !important;
          vertical-align: middle !important;
        }
        .card-item .info-match .info-conner i {
          font-size: 8px !important;
          color: white !important;
          margin: 0 !important;
          line-height: 1 !important;
        }
        .card-item .info-match .info-conner i.yellow-card {
          color: #FFD700 !important;
        }
        .card-item .info-match .info-conner span {
          font-weight: 400 !important;
          font-size: 9px !important;
          color: white !important;
          margin: 0 !important;
          line-height: 1 !important;
        }
        .card-item .card-footer {
          border-top: 1px solid rgba(246, 137, 34, 1);
          padding: 10px;
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
        }
        .card-item .card-footer .caster-icon {
          width: 60px;
        }
        .card-item .card-footer .caster-info img {
          width: 25px;
          height: 25px;
          object-fit: cover;
          border-radius: 50%;
          border: 1px solid rgba(246, 137, 34, 1);
        }
        .card-item .card-footer .caster-info p {
          color: white;
          margin: 0;
          font-weight: 700;
          line-height: 1;
        }
        .card-item .card-footer .img-ods {
          width: 130px;
          border-radius: 5px;
        }
        .card-item .icon-vs {
          height: 25px;
        }
        .card-item .vs > div {
          margin-left: auto;
          margin-right: auto;
        }
        .card-item .vs .ods-icon {
          width: 80px;
          margin-bottom: 5px;
          margin-left: auto;
          margin-right: auto;
          display: block;
        }
        img {
          opacity: 1;
          transition: opacity 1s;
          display: inline-block;
          height: auto;
          max-width: 100%;
          vertical-align: middle;
        }
        .nav-tabs {
          border: none;
        }
        .tab-menu {
          background: rgba(255, 170, 2, 1);
          padding: 8px 8px 0 8px;
          border-top-left-radius: 10px;
          border-top-right-radius: 10px;
          border-bottom: 1px solid white;
          display: flex;
          justify-content: space-between;
          align-items: stretch;
          height: auto;
          width: 100%;
        }
        .tab-items-group {
          display: flex;
          align-items: stretch;
          gap: 0;
          flex: 6;
        }
        .betting-banner {
          display: flex;
          align-items: center;
          margin-left: auto;
          flex: 4;
          justify-content: flex-end;
        }
        @media (max-width: 768px) {
          .betting-banner {
            display: none;
          }
          .tab-items-group {
            flex: 1;
          }
        }
        .nav-link {
          border: none !important;
          background: transparent !important;
          margin-bottom: 0 !important;
        }
        .nav-link.active {
          color: #FC5D1D !important;
          background: white !important;
          border: none !important;
          border-top-left-radius: 8px !important;
          border-top-right-radius: 8px !important;
          border-bottom-left-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        }
        .tab-menu-item {
          min-height: 60px;
          margin: 0;
          flex: 1;
          max-width: 120px;
        }
        .tab-menu-item button {
          background-color: transparent;
          color: white;
          line-height: normal;
          text-transform: capitalize;
          width: 100%;
          height: 100%;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 8px 12px;
          transition: all 0.3s ease;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          border: none;
          margin: 0;
        }
        .tab-menu-item button.active,
        .tab-menu-item button:hover {
          background-color: white;
          color: rgba(255, 170, 2, 1);
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
        .tab-menu-item button.active p,
        .tab-menu-item button:hover p {
          color: rgba(255, 170, 2, 1);
        }
        .tab-menu-item button span {
          color: white;
          background: rgba(252, 93, 29, 1);
          border-radius: 50%;
          font-size: 10px;
          min-width: 18px;
          height: 18px;
          line-height: 18px;
          position: absolute;
          right: 8px;
          top: 4px;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 4px;
        }
        .tab-menu-item button.active span {
          background: rgba(252, 93, 29, 1);
          color: white;
        }
        .tab-menu-item button.active i,
        .tab-menu-item button:hover i {
          color: rgba(252, 93, 29, 1);
        }
        .tab-menu-item button i {
          font-size: 20px;
          transition: color 0.3s ease;
        }
        .tab-menu-item button p {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          text-align: center;
        }
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }
        .pointer {
          cursor: pointer;
        }
        .gap-2 {
          gap: 0.5rem;
        }
        .gap-3 {
          gap: 1rem;
        }
        .mt-1 {
          margin-top: 0.25rem;
        }
        .mt-2 {
          margin-top: 0.5rem;
        }
        .pb-3 {
          padding-bottom: 1rem;
        }
        .ps-5 {
          padding-left: 3rem;
        }
        .w-50 {
          width: 50%;
        }
        .justify-center {
          justify-content: center;
        }
        .justify-end {
          justify-content: flex-end;
        }
        .justify-start {
          justify-content: flex-start;
        }
        .text-center {
          text-align: center;
        }
        .card-item .vs {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          flex-direction: column !important;
          text-align: center !important;
        }
        .card-item .vs .info-match {
          margin-left: auto !important;
          margin-right: auto !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
        }
        @media (min-width: 768px) {
          .card-item .vs {
           
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            flex-direction: column !important;
          }
          .card-item .vs .info-match {
            position: relative !important;
            left: 0 !important;
            transform: none !important;
          }
        }
        /* Skeleton Styles */
        .skeleton-card {
          height: 180px;
          background: #f0f0f0;
          border-radius: 10px;
          position: relative;
          overflow: hidden;
        }
        .skeleton-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.3) 20%, rgba(255, 255, 255, 0.6) 60%, rgba(255, 255, 255, 0) 100%);
          animation: shimmer 1.5s infinite linear;
        }
        .skeleton-league {
          background: #e0e0e0;
          width: 154px;
          height: 30px;
          border-radius: 5px;
        }
        .skeleton-league-text {
          background: #d0d0d0;
          height: 16px;
          width: 100px;
          border-radius: 3px;
        }
        .skeleton-time {
          background: #e0e0e0;
          height: 20px;
          width: 81px;
          border-radius: 30px;
        }
        .skeleton-team-name {
          background: #e0e0e0;
          height: 16px;
          width: 100px;
          border-radius: 3px;
        }
        .skeleton-avatar {
          background: #d0d0d0;
          width: 50px;
          height: 50px;
          border-radius: 50%;
        }
        .skeleton-vs-icon {
          background: #e0e0e0;
          width: 80px;
          height: 25px;
          border-radius: 3px;
          margin-bottom: 5px;
        }
        .skeleton-vs-text {
          background: #d0d0d0;
          height: 16px;
          width: 100px;
          border-radius: 3px;
        }
        .skeleton-caster-icon {
          background: #e0e0e0;
          width: 60px;
          height: 25px;
          border-radius: 3px;
        }
        .skeleton-caster-avatar {
          background: #d0d0d0;
          width: 25px;
          height: 25px;
          border-radius: 50%;
        }
        .skeleton-caster-name {
          background: #d0d0d0;
          height: 16px;
          width: 100px;
          border-radius: 3px;
        }
        .skeleton-banner {
          background: #e0e0e0;
          width: 130px;
          height: 25px;
          border-radius: 5px;
        }
        .skeleton-icon {
          background: #d0d0d0;
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        .skeleton-score {
          background: #d0d0d0;
          height: 9px;
          width: 20px;
          border-radius: 3px;
        }
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        /* Mobile team layout */
        @media only screen and (max-width: 549px) {
          .info-team {
            width: 35%;
            min-width: 120px;
          }
          
          /* Hide betting banner on mobile */
          .betting-banner {
            display: none !important;
          }
        }
      `}</style>
    </>
  )
} 